import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '@/constants/storage';
import type { StoreResponse } from '@/types/store';

export type ThemeMode = 'light' | 'dark';

interface AppState {
  selectedStore: StoreResponse | null;
  theme: ThemeMode;
  sidebarCollapsed: boolean;
  /** Bumped when org modules change so layouts reload navigation */
  navModulesVersion: number;
}

function loadTheme(): ThemeMode {
  const t = localStorage.getItem(STORAGE_KEYS.THEME);
  return t === 'dark' ? 'dark' : 'light';
}

function syncThemeClass(theme: ThemeMode) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

syncThemeClass(loadTheme());

function loadStore(): StoreResponse | null {
  const raw = localStorage.getItem(STORAGE_KEYS.SELECTED_STORE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoreResponse;
  } catch {
    return null;
  }
}

const initialState: AppState = {
  selectedStore: loadStore(),
  theme: loadTheme(),
  sidebarCollapsed: false,
  navModulesVersion: 0,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setSelectedStore: (state, action: PayloadAction<StoreResponse | null>) => {
      state.selectedStore = action.payload;
      if (action.payload) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_STORE, JSON.stringify(action.payload));
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_STORE);
      }
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      localStorage.setItem(STORAGE_KEYS.THEME, action.payload);
      document.documentElement.classList.toggle('dark', action.payload === 'dark');
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    bumpNavModules: (state) => {
      state.navModulesVersion += 1;
    },
  },
});

export const { setSelectedStore, setTheme, toggleSidebar, setSidebarCollapsed, bumpNavModules } =
  appSlice.actions;
export default appSlice.reducer;
